import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { inr, monthLabel } from "../lib/format";
import { CONTACT } from "../components/site/Footer";

const Rows = ({ title, rows, total, totalLabel }) => (
  <table className="w-full text-sm">
    <thead><tr className="border-b-2 border-[#0e0f0c]"><th className="py-2 text-left">{title}</th><th className="py-2 text-right">Amount</th></tr></thead>
    <tbody>
      {rows.map((r, i) => <tr key={i} className="border-b border-[#d9d3c4]"><td className="py-2">{r.label}</td><td className="py-2 text-right">{inr(r.amount)}</td></tr>)}
      <tr className="font-semibold"><td className="py-2">{totalLabel}</td><td className="py-2 text-right">{inr(total)}</td></tr>
    </tbody>
  </table>
);

export default function PayslipView() {
  const { id } = useParams();
  const { user } = useAuth();
  const [slip, setSlip] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) api.get(`/payslips/${id}`).then((r) => setSlip(r.data)).catch((e) => setError(formatApiError(e)));
    return undefined;
  }, [user, id]);

  if (user === null) return <div className="min-h-screen bg-[var(--ink)]" />;
  if (!user) return <Navigate to="/employee/login" replace />;
  const back = user.role === "admin" ? `/admin/employees/${slip?.employee_id ?? ""}` : "/employee";

  return (
    <main className="min-h-screen bg-[var(--ink)] px-4 py-8 print:bg-white print:p-0" data-testid="payslip-view-page">
      <div className="mx-auto mb-6 flex max-w-3xl items-center justify-between print:hidden">
        <Link to={back} className="flex items-center gap-2 text-sm text-[var(--bone-2)] hover:text-[var(--signal)]" data-testid="payslip-back"><ArrowLeft className="h-4 w-4" /> Back</Link>
        <button onClick={() => window.print()} disabled={!slip} data-testid="payslip-print-button"
          className="flex items-center gap-2 rounded-full bg-[var(--signal)] px-5 py-2.5 text-sm font-semibold text-[var(--ink)]">
          <Printer className="h-4 w-4" /> Print / Save as PDF
        </button>
      </div>
      {error && <p className="text-center text-red-300" data-testid="payslip-error">{error}</p>}
      {slip && (
        <article className="mx-auto max-w-3xl bg-white p-6 text-[#0e0f0c] md:p-10 print:max-w-none print:p-6" data-testid="payslip-document">
          <header className="flex items-start justify-between gap-6 border-b-2 border-[#0e0f0c] pb-5">
            <div className="flex items-center gap-4">
              <img src="/logo.png" alt="BHISM logo" className="h-14 w-14 object-contain" />
              <div>
                <p className="font-display text-2xl font-black uppercase leading-none text-[#164a9c]">BHISM Infratech Pvt. Ltd.</p>
                <p className="mt-1 text-xs text-[#3d3a33]">{CONTACT.address}</p>
                <p className="text-xs text-[#3d3a33]">{CONTACT.phone} · {CONTACT.email}</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xs uppercase tracking-widest text-[#3d3a33]">Salary slip</p>
              <p className="font-display text-2xl font-black uppercase" data-testid="payslip-month">{monthLabel(slip.month)}</p>
            </div>
          </header>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3 py-5 text-sm sm:grid-cols-3">
            {[["Employee", slip.name], ["Employee ID", slip.emp_code], ["Designation", slip.designation || "—"], ["Site", slip.site || "—"], ["Working days", slip.working_days], ["Paid days", slip.paid_days]].map(([k, v]) => (
              <div key={k}><dt className="text-xs uppercase tracking-wider text-[#5c584f]">{k}</dt><dd className="font-medium">{v}</dd></div>
            ))}
          </dl>
          <div className="grid gap-8 border-t border-[#d9d3c4] pt-5 sm:grid-cols-2">
            <Rows title="Earnings" rows={slip.earnings} total={slip.gross} totalLabel="Gross earnings" />
            <Rows title="Deductions" rows={slip.deductions} total={slip.total_deductions} totalLabel="Total deductions" />
          </div>
          <div className="mt-8 flex items-center justify-between bg-[#0e0f0c] px-5 py-4 text-white print:border-2 print:border-[#0e0f0c] print:bg-white print:text-[#0e0f0c]">
            <span className="text-sm uppercase tracking-widest">Net pay</span>
            <span className="font-display text-3xl font-black" data-testid="payslip-net">{inr(slip.net)}</span>
          </div>
          {slip.note && <p className="mt-4 text-sm text-[#3d3a33]">Note: {slip.note}</p>}
          <p className="mt-8 text-xs text-[#5c584f]">This is a computer-generated salary slip and does not require a signature.</p>
        </article>
      )}
    </main>
  );
}
